import React, { useState, useCallback } from "react";
import { FiInfo, FiLock } from "react-icons/fi";
import Modal from "../../../tailadmin/components/ui/Modal";
import { Chat } from "../../../types/conversations";
import conversationsService from "../../../services/conversations";

interface PermissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  chat: Chat;
  onLinkSuccess?: () => void;
}

export const PermissionModal: React.FC<PermissionModalProps> = ({
  isOpen,
  onClose,
  chat,
  onLinkSuccess,
}) => {
  const [step, setStep] = useState<"intro" | "password">("intro");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setStep("intro");
    setPassword("");
    setError(null);
    onClose();
  }, [onClose]);

  const handleTornarAluno = useCallback(() => {
    setStep("password");
    setError(null);
    setPassword("");
  }, []);

  const handleVoltar = useCallback(() => {
    setStep("intro");
    setError(null);
    setPassword("");
  }, []);

  const handleConfirmar = useCallback(async () => {
    const pwd = password.trim();
    if (!pwd) {
      setError("Digite sua senha para continuar.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await conversationsService.linkStudentToChat(chat.id, pwd);
      if (result.success) {
        handleClose();
        onLinkSuccess?.();
      } else {
        setError(result.error ?? "Não foi possível vincular. Tente novamente.");
      }
    } catch {
      setError("Erro ao conectar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, [chat.id, password, onLinkSuccess, handleClose]);

  const title = step === "intro" ? "Permissão necessária" : "Confirmar com sua senha";

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title}>
      <div className="flex flex-col h-full min-h-0">
        {/* Conteúdo centralizado verticalmente */}
        <div className="flex-1 flex flex-col justify-center px-4 py-6 sm:px-6 sm:py-8">
          {step === "intro" ? (
            <div className="flex flex-col items-center gap-6 sm:gap-8 max-w-md mx-auto w-full">
              {/* Ícone */}
              <div className="flex h-20 w-20 sm:h-24 sm:w-24 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30 ring-8 sm:ring-12 ring-amber-50 dark:ring-amber-900/10 flex-shrink-0">
                <FiInfo
                  size={36}
                  className="text-amber-600 dark:text-amber-400 sm:w-12 sm:h-12"
                />
              </div>

              {/* Texto */}
              <div className="text-center space-y-3 sm:space-y-4 w-full">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  Acesso restrito
                </h3>
                <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                  Você não tem permissão para visualizar o perfil deste usuário.
                  Vincule-o como seu aluno para ter acesso.
                </p>
              </div>

              {/* Botões */}
              <div className="flex flex-col gap-3 w-full mt-2">
                <button
                  type="button"
                  onClick={handleTornarAluno}
                  className="w-full min-h-[56px] sm:min-h-[60px] rounded-2xl bg-purple-600 px-6 py-4 text-base sm:text-lg font-semibold text-white transition-all duration-200 hover:bg-purple-700 active:scale-[0.98] dark:bg-purple-500 dark:hover:bg-purple-600 shadow-lg hover:shadow-xl touch-manipulation"
                >
                  Tornar meu aluno
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="w-full min-h-[52px] sm:min-h-[56px] rounded-2xl border-2 border-gray-300 dark:border-gray-600 px-6 py-3.5 text-base font-medium text-gray-700 dark:text-gray-300 bg-transparent hover:bg-gray-50 dark:hover:bg-gray-800 active:scale-[0.98] transition-all touch-manipulation"
                >
                  Entendi
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-6 sm:gap-8 max-w-md mx-auto w-full">
              {/* Ícone */}
              <div className="flex h-20 w-20 sm:h-24 sm:w-24 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30 ring-8 sm:ring-12 ring-purple-50 dark:ring-purple-900/10 flex-shrink-0 mx-auto">
                <FiLock
                  size={36}
                  className="text-purple-600 dark:text-purple-400 sm:w-12 sm:h-12"
                />
              </div>

              {/* Texto */}
              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 text-center leading-relaxed">
                Digite sua senha de profissional para confirmar o vínculo.
              </p>

              {/* Input */}
              <div className="space-y-3">
                <label
                  htmlFor="permission-modal-password"
                  className="block text-base font-medium text-gray-700 dark:text-gray-300"
                >
                  Sua senha
                </label>
                <input
                  id="permission-modal-password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError(null);
                  }}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  disabled={loading}
                  className="w-full min-h-[56px] sm:min-h-[60px] rounded-2xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-5 py-4 text-base sm:text-lg text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none touch-manipulation disabled:opacity-60 transition-all"
                  autoFocus
                />
                {error && (
                  <p className="text-sm sm:text-base text-red-600 dark:text-red-400 font-medium">
                    {error}
                  </p>
                )}
              </div>

              {/* Botões */}
              <div className="flex flex-col gap-3 w-full mt-2">
                <button
                  type="button"
                  onClick={handleConfirmar}
                  disabled={loading}
                  className="w-full min-h-[56px] sm:min-h-[60px] rounded-2xl bg-purple-600 px-6 py-4 text-base sm:text-lg font-semibold text-white transition-all duration-200 hover:bg-purple-700 active:scale-[0.98] disabled:opacity-70 disabled:pointer-events-none touch-manipulation shadow-lg hover:shadow-xl"
                >
                  {loading ? "Confirmando…" : "Confirmar"}
                </button>
                <button
                  type="button"
                  onClick={handleVoltar}
                  disabled={loading}
                  className="w-full min-h-[52px] sm:min-h-[56px] rounded-2xl border-2 border-gray-300 dark:border-gray-600 px-6 py-3.5 text-base font-medium text-gray-700 dark:text-gray-300 bg-transparent hover:bg-gray-50 dark:hover:bg-gray-800 active:scale-[0.98] transition-all touch-manipulation disabled:opacity-60"
                >
                  Voltar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
